/* Import Estilo */
import './index.scss';

/* Import Bibliotecas */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

/* Import Contextos */
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useFavorites } from '../../context/FavoritesContext';
import { useToast } from '../../context/ToastContext';

/* Import Componentes */
import Cabecalho from '../../components/Cabecalho';
import Rodape from '../../components/Rodape';
import CardProduto from '../../components/CardProd';

/* Import Requisição API */
import api from '../../api';
import { formatarPrecoUnitario, deveExibirPrecoUnitario } from '../../utils/precoUnitario';
import { formatarDescricaoLonga } from '../../utils/descricaoLonga';

// Função para normalizar acentos
const normalizarString = (str) => {
  if (!str) return '';
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
};

export default function VisualizarProduto() {
  /* Variáveis auxiliares */
  const { id } = useParams();
  const location = useLocation();
  const { userId } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();

  /* Estados de Produto */
  const [produto, setProduto] = useState();
  const [produtosRelacionados, setProdutosRelacionados] = useState([]);
  const [produtosRecentes, setProdutosRecentes] = useState([]);
  const [error, setError] = useState('');
  const [selectedVar, setSelectedVar] = useState(null);
  const [primaryImg, setPrimaryImg] = useState('');
  const [tab, setTab] = useState('descricao');
  const [dragStartX, setDragStartX] = useState(null);
  const [lightboxAberto, setLightboxAberto] = useState(false);
  const [zoomNivel, setZoomNivel] = useState(1);
  const houveSwipeRef = useRef(false);

  /* Estados de Variações Selecionadas */
  const [corSel, setCorSel] = useState('');
  const [tamSel, setTamSel] = useState('');
  const [matSel, setMatSel] = useState('');
  const [tipoSel, setTipoSel] = useState('');
  const [lastChanged, setLastChanged] = useState(null);

  /* Contextos */
  const { items, addToCart, updateQuantity } = useCart();
  const { favorites, toggleFavorite } = useFavorites();

  useEffect(() => {
    async function buscarProdutoERecomendacoes() {
      if (!id) return;

      try {
        const produtoRes = userId
          ? await api.get(`/produtocliente/${id}`, { params: { usuarioId: userId } })
          : await api.get(`/produtopublico/${id}`);

        if (!produtoRes.data.success || !produtoRes.data.produto) {
          console.error('Produto não encontrado ou resposta inválida:', produtoRes.data.message);
          navigate('/produtos');
          return;
        }

        const raw = produtoRes.data.produto;
        const variacoes = (raw.variacoes || []).map(v => ({
          id: v.id,
          tamanho: v.tamanho,
          cor: v.cor,
          material: v.material,
          tipo: v.tipo,
          unidades_por_pacote: v.unidades_por_pacote,
          preco: v.preco,
          estoque: v.estoque,
          imagens: (v.imagens || []).map(img => ({
            url: typeof img.url === 'string' ? img.url : img.url?.url,
            principal: img.principal ?? false
          }))
        }));

        if (!variacoes.length) {
          console.error('Produto sem variações.');
          navigate('/produtos');
          return;
        }

        setProduto({ ...raw, variacoes });

        const primeiraVar = variacoes[0];
        setSelectedVar(primeiraVar.id);
        setCorSel(primeiraVar.cor);
        setTamSel(primeiraVar.tamanho);
        setMatSel(primeiraVar.material);
        setTipoSel(primeiraVar.tipo || '');
        setPrimaryImg(primeiraVar.imagens[0]?.url || '');

        // Recomendações em bloco separado para não quebrar o fluxo principal
        try {
          const recomendacaoRes = await api.get('/recomendacaoprodutos', {
            params: {
              ...(userId ? { usuario_id: userId } : {}),
              produto_id: id,
              categoria_id: raw.id_categoria
            }
          });

          const produtosRecomendados = Array.isArray(recomendacaoRes.data?.produtos)
            ? recomendacaoRes.data.produtos
            : [];

          const prodsRec = produtosRecomendados.slice(0, 20).map(rec => {
            const firstImg = Array.isArray(rec.imagens)
              ? (typeof rec.imagens[0] === 'string' ? rec.imagens[0] : rec.imagens[0]?.url)
              : rec.imagem_principal;
            const variacoesRec = Array.isArray(rec.variacoes) && rec.variacoes.length > 0
              ? rec.variacoes
              : null;
            const variacaoApi = variacoesRec ? variacoesRec[0] : null;
            const fonte = variacaoApi ?? rec;
            const produtoId = rec.id_produto
              ?? rec.id
              ?? rec.produto_id
              ?? rec.produtoId
              ?? variacaoApi?.id_produto
              ?? variacaoApi?.produto_id
              ?? variacaoApi?.produtoId;
            const variacaoId = variacaoApi?.id_variacao
              ?? variacaoApi?.id
              ?? rec.id_variacao
              ?? rec.variacao_id
              ?? rec.id_variacao_produto
              ?? rec.idVariacao;
            const precoFinal = fonte?.preco
              ?? rec.preco_varejo
              ?? rec.preco;
            const unidadesPorPacote = fonte?.unidades_por_pacote
              ?? rec.unidades_por_pacote
              ?? rec.unidadesPorPacote;
            const estoqueFinal = fonte?.estoque
              ?? rec.estoque
              ?? 0;
            const corFinal = fonte?.cor ?? rec.cor;
            const tamanhoFinal = fonte?.tamanho ?? rec.tamanho;
            const materialFinal = fonte?.material ?? rec.material;
            const tipoFinal = fonte?.tipo ?? rec.tipo;

            return {
              id_variacao: variacaoId,
              id: produtoId,
              id_produto: produtoId,
              nome: rec.nome,
              preco: precoFinal,
              imagem_principal: firstImg || fonte?.imagem_principal || '',
              cor: corFinal,
              tamanho: tamanhoFinal,
              material: materialFinal,
              tipo: tipoFinal,
              estoque: estoqueFinal,
              unidades_por_pacote: unidadesPorPacote,
              variacoes: variacoesRec || undefined
            };
          });

          setProdutosRelacionados(prodsRec);
        } catch (recError) {
          console.warn('Não foi possível carregar recomendações:', recError.message);
        }

      } catch (error) {
        console.error('Erro ao carregar dados do produto:', error);
        if (error.response?.status === 404) {
          navigate('/produtos');
          return;
        }
        setError('Nao foi possivel carregar o produto. Tente novamente em instantes.');
      }
    }

    buscarProdutoERecomendacoes();
  }, [id, userId, navigate]);

  useEffect(() => {
    if (!produto) return;

    const variationAtual = produto.variacoes.find(v => v.id === selectedVar) || produto.variacoes[0];
    const item = {
      id_produto: produto.id,
      id_variacao: variationAtual?.id,
      nome: produto.nome,
      preco: variationAtual?.preco || 0,
      estoque: Number(variationAtual?.estoque) || 0,
      imagem_principal: variationAtual?.imagens?.[0]?.url || '',
      cor: variationAtual?.cor || '',
      tamanho: variationAtual?.tamanho || '',
      material: variationAtual?.material || '',
      tipo: variationAtual?.tipo || '',
      visualizado_em: new Date().toISOString()
    };

    const listaAtual = JSON.parse(localStorage.getItem('produtosRecentes') || '[]');
    const semDuplicados = listaAtual.filter(p => String(p.id_produto) !== String(item.id_produto));
    const novaLista = [item, ...semDuplicados].slice(0, 12);
    localStorage.setItem('produtosRecentes', JSON.stringify(novaLista));

    const paraExibir = novaLista.filter(p => String(p.id_produto) !== String(item.id_produto)).slice(0, 8);
    setProdutosRecentes(paraExibir);
  }, [produto, selectedVar]);

  // Efeito para selecionar variação a partir da query string
  useEffect(() => {
    if (!produto) return;
    
    const searchParams = new URLSearchParams(location.search);
    const variacaoId = searchParams.get('variacao');
    
    if (variacaoId) {
      const variacaoEncontrada = produto.variacoes.find(v => String(v.id) === String(variacaoId));
      if (variacaoEncontrada) {
        setSelectedVar(variacaoEncontrada.id);
        setCorSel(variacaoEncontrada.cor);
        setTamSel(variacaoEncontrada.tamanho);
        setMatSel(variacaoEncontrada.material);
        setTipoSel(variacaoEncontrada.tipo || '');
        setPrimaryImg(variacaoEncontrada.imagens[0]?.url || '');
      }
    }
  }, [produto, location.search]);

  const encontrarVariacao = (variacoes, base, chavePrimaria) => {
    console.log('📦 Variações disponíveis:', variacoes.map(v => ({ id: v.id, cor: v.cor, tamanho: v.tamanho, material: v.material, tipo: v.tipo })));
    const chaves = ['cor', 'tamanho', 'material', 'tipo'];
    const match = (v, filtros) => {
      const resultado = 
        (!filtros.cor || normalizarString(v.cor) === normalizarString(filtros.cor)) &&
        (!filtros.tamanho || normalizarString(v.tamanho) === normalizarString(filtros.tamanho)) &&
        (!filtros.material || normalizarString(v.material) === normalizarString(filtros.material)) &&
        (!filtros.tipo || normalizarString(v.tipo) === normalizarString(filtros.tipo));
      
      if (resultado) {
        console.log('✓ Match encontrado para filtros:', filtros, '-> variação:', { id: v.id, cor: v.cor, tamanho: v.tamanho });
      }
      return resultado;
    };

    const toFiltros = (keys) => {
      const filtros = {};
      keys.forEach(k => {
        if (base[k]) filtros[k] = base[k];
      });
      return filtros;
    };

    const candidatos = [];
    candidatos.push(toFiltros(chaves));

    if (chavePrimaria) {
      const outras = chaves.filter(k => k !== chavePrimaria);
      outras.forEach(remover => {
        candidatos.push(toFiltros([chavePrimaria, ...outras.filter(k => k !== remover)]));
      });
      outras.forEach(k => candidatos.push(toFiltros([chavePrimaria, k])));
      candidatos.push(toFiltros([chavePrimaria]));
    }

    const extras = [
      ['cor', 'tamanho', 'material'],
      ['cor', 'tamanho', 'tipo'],
      ['cor', 'material', 'tipo'],
      ['tamanho', 'material', 'tipo'],
      ['cor', 'tamanho'],
      ['cor', 'material'],
      ['cor', 'tipo'],
      ['tamanho', 'material'],
      ['tamanho', 'tipo'],
      ['material', 'tipo'],
      ['cor'],
      ['tamanho'],
      ['material'],
      ['tipo']
    ];
    extras.forEach(keys => candidatos.push(toFiltros(keys)));

    for (const filtros of candidatos) {
      if (!Object.keys(filtros).length) continue;
      const encontrada = variacoes.find(v => match(v, filtros));
      if (encontrada) return encontrada;
    }

    return variacoes[0] || null;
  };

  useEffect(() => {
    if (!produto) return;
    const base = {
      cor: corSel,
      tamanho: tamSel,
      material: matSel,
      tipo: tipoSel
    };
    console.log('🔍 Buscando variação com:', { base, lastChanged });
    const vari = encontrarVariacao(produto.variacoes, base, lastChanged);
    console.log('✅ Variação encontrada:', vari);
    if (vari) {
      setSelectedVar(vari.id);
      setPrimaryImg(vari.imagens[0]?.url || '');

      if (normalizarString(vari.cor) !== normalizarString(corSel)) {
        setCorSel(vari.cor || '');
      }
      if (normalizarString(vari.tamanho) !== normalizarString(tamSel)) {
        setTamSel(vari.tamanho || '');
      }
      if (normalizarString(vari.material) !== normalizarString(matSel)) {
        setMatSel(vari.material || '');
      }
      if (normalizarString(vari.tipo) !== normalizarString(tipoSel)) {
        setTipoSel(vari.tipo || '');
      }
    } else {
      console.error('❌ Nenhuma variação encontrada para:', base);
    }
  }, [corSel, tamSel, matSel, tipoSel, lastChanged, produto]);

  const trocarImagemPorPasso = useCallback((passo) => {
    const variacaoAtual = produto?.variacoes?.find((v) => v.id === selectedVar);
    const imagensAtuais = variacaoAtual?.imagens || [];
    if (!imagensAtuais.length) return;

    const indiceAtual = imagensAtuais.findIndex((img) => img.url === primaryImg);
    const indiceBase = indiceAtual >= 0 ? indiceAtual : 0;
    const proximoIndice = (indiceBase + passo + imagensAtuais.length) % imagensAtuais.length;
    setPrimaryImg(imagensAtuais[proximoIndice].url);
  }, [produto, selectedVar, primaryImg]);

  const iniciarArraste = (clientX) => {
    setDragStartX(clientX);
  };

  const finalizarArraste = (clientX) => {
    if (dragStartX === null) return;

    const diferenca = dragStartX - clientX;
    const limiteTroca = 50;

    if (Math.abs(diferenca) > limiteTroca) {
      trocarImagemPorPasso(diferenca > 0 ? 1 : -1);
      houveSwipeRef.current = true;
    }

    setDragStartX(null);
  };

  const abrirLightbox = () => {
    if (!primaryImg) return;
    setLightboxAberto(true);
    setZoomNivel(1);
  };

  const fecharLightbox = useCallback(() => {
    setLightboxAberto(false);
    setZoomNivel(1);
  }, []);

  const zoomIn = useCallback(() => setZoomNivel((prev) => Math.min(4, Math.max(1, prev + 0.25))), []);
  const zoomOut = useCallback(() => setZoomNivel((prev) => Math.min(4, Math.max(1, prev - 0.25))), []);
  const resetZoom = useCallback(() => setZoomNivel(1), []);

  const onWheelZoom = (e) => {
    e.preventDefault();
    const ajuste = e.deltaY < 0 ? 0.2 : -0.2;
    setZoomNivel((prev) => Math.min(4, Math.max(1, prev + ajuste)));
  };

  useEffect(() => {
    if (!lightboxAberto) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        fecharLightbox();
      }
      if (e.key === 'ArrowRight') {
        trocarImagemPorPasso(1);
      }
      if (e.key === 'ArrowLeft') {
        trocarImagemPorPasso(-1);
      }
      if (e.key === '+' || e.key === '=') {
        zoomIn();
      }
      if (e.key === '-') {
        zoomOut();
      }
      if (e.key === '0') {
        resetZoom();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [lightboxAberto, primaryImg, selectedVar, fecharLightbox, trocarImagemPorPasso, zoomIn, zoomOut, resetZoom]);

  if (error) {
    return <div className="error-page">{error}</div>;
  }
  if (!produto) {
    return null;
  }

  const variation = produto.variacoes.find(v => v.id === selectedVar);
  const imagens = variation.imagens;

  const normalizeValor = (valor) => (typeof valor === 'string' ? valor.trim() : valor);
  const filtrarValores = (valores) => valores.map(normalizeValor).filter(Boolean);
  const isEmEstoque = (estoque) => Number(estoque) > 0;
  const compararValor = (a, b) => normalizarString(a) === normalizarString(b);

  const totalEstoque = produto.variacoes.reduce(
    (sum, v) => sum + (Number(v.estoque) || 0),
    0
  );
  const produtoIndisponivel = totalEstoque <= 0;
  const variacaoIndisponivel = !isEmEstoque(variation?.estoque);
  const podeComprar = !produtoIndisponivel && !variacaoIndisponivel;

  const matchesFiltro = (variacao, filtros) =>
    (!filtros.cor || compararValor(variacao.cor, filtros.cor)) &&
    (!filtros.tamanho || compararValor(variacao.tamanho, filtros.tamanho)) &&
    (!filtros.material || compararValor(variacao.material, filtros.material)) &&
    (!filtros.tipo || compararValor(variacao.tipo, filtros.tipo));

  const optionDisponivel = (chave, valor) => {
    const filtros = {
      cor: corSel,
      tamanho: tamSel,
      material: matSel,
      tipo: tipoSel,
      [chave]: valor
    };

    return produto.variacoes.some(
      (v) => isEmEstoque(v.estoque) && matchesFiltro(v, filtros)
    );
  };

  const coresAll = Array.from(new Set(filtrarValores(produto.variacoes.map(v => v.cor))));
  const tamAll = Array.from(new Set(filtrarValores(produto.variacoes.map(v => v.tamanho))));
  const matAll = Array.from(new Set(filtrarValores(produto.variacoes.map(v => v.material))));
  const tipoAll = Array.from(new Set(filtrarValores(produto.variacoes.map(v => v.tipo))));

  const showVariacoes =
    (coresAll.length > 0 || tamAll.length > 0 || matAll.length > 0 || tipoAll.length > 0);

  const cartItem = selectedVar
    ? items.find(item => item.id === selectedVar)
    : null;
  const inCart = Boolean(cartItem);
  const quantidade = inCart ? cartItem.quantidade : 0;

  const isFavorito = selectedVar
    ? favorites.includes(selectedVar)
    : false;

  const descricaoCurtaHtml = formatarDescricaoLonga(produto.descricao || produto.descricao_longa || '');
  const descricaoLongaHtml = formatarDescricaoLonga(produto.descricao_longa || '-');

  const handleToggleFavorite = () => {
    if (!selectedVar || !userId) return;
    toggleFavorite({ id_usuario: userId, id_variacao: selectedVar });
    showToast(
      isFavorito ? 'Removido dos favoritos!' : 'Adicionado aos favoritos!',
      'success'
    );
  };

  return (
    <>
      <Cabecalho />
      <main className="visualizar-produto">
        <section className="produto-view">
          <div className="faixa-1">
            {/* Título visível apenas no mobile */}
            <div className="mobile-product-title">
              <h1>{produto.nome}</h1>
            </div>
            
            <div className="galeria">
              <div 
                className="imagem-principal"
                onMouseDown={(e) => iniciarArraste(e.clientX)}
                onMouseUp={(e) => finalizarArraste(e.clientX)}
                onMouseLeave={() => setDragStartX(null)}
                onTouchStart={(e) => iniciarArraste(e.touches[0].clientX)}
                onTouchEnd={(e) => finalizarArraste(e.changedTouches[0].clientX)}
                onClick={() => {
                  if (houveSwipeRef.current) {
                    houveSwipeRef.current = false;
                    return;
                  }
                  abrirLightbox();
                }}
                style={{ cursor: dragStartX !== null ? 'grabbing' : 'grab' }}
              >
                <img className="principal" src={primaryImg} alt={produto.nome} />
                <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    className={`btn-share`}
                    title="Compartilhar"
                    onClick={() => {
                      if (navigator.share) {
                        navigator.share({
                          title: produto.nome,
                          text: produto.descricao || '',
                          url: window.location.href
                        });
                      } else {
                        navigator.clipboard.writeText(window.location.href);
                        showToast('Link copiado para a área de transferência!', 'success');
                      }
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10.833 5.00016L12.4997 3.3335C13.333 2.50016 14.9997 2.50016 15.833 3.3335L16.6663 4.16683C17.4997 5.00016 17.4997 6.66683 16.6663 7.50016L12.4997 11.6668C11.6663 12.5002 9.99967 12.5002 9.16634 11.6668M9.16634 15.0002L7.49967 16.6668C6.66634 17.5002 4.99967 17.5002 4.16634 16.6668L3.33301 15.8335C2.49967 15.0002 2.49967 13.3335 3.33301 12.5002L7.49967 8.3335C8.33301 7.50016 9.99967 7.50016 10.833 8.3335" stroke="#000" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </button>
                  <button
                    type="button"
                    className={`btn-fav ${isFavorito ? 'active' : ''}`}
                    onClick={handleToggleFavorite}
                  >
                    {isFavorito ? (
                      <svg width="19" height="17" viewBox="0 0 19 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15.1667 10.1667C16.4083 8.95 17.6667 7.49167 17.6667 5.58333C17.6667 4.36776 17.1838 3.20197 16.3242 2.34243C15.4647 1.48289 14.2989 1 13.0833 1C11.6167 1 10.5833 1.41667 9.33333 2.66667C8.08333 1.41667 7.05 1 5.58333 1C4.36776 1 3.20197 1.48289 2.34243 2.34243C1.48289 3.20197 1 4.36776 1 5.58333C1 7.5 2.25 8.95833 3.5 10.1667L9.33333 16L15.1667 10.1667Z" fill="#FF0000" stroke="black" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ) : (
                      <svg width="20" height="18" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15.8333 10.6667C17.075 9.45 18.3333 7.99167 18.3333 6.08333C18.3333 4.86776 17.8504 3.70197 16.9909 2.84243C16.1313 1.98289 14.9655 1.5 13.75 1.5C12.2833 1.5 11.25 1.91667 9.99996 3.16667C8.74996 1.91667 7.71663 1.5 6.24996 1.5C5.03438 1.5 3.86859 1.98289 3.00905 2.84243C2.14951 3.70197 1.66663 4.86776 1.66663 6.08333C1.66663 8 2.91663 9.45833 4.16663 10.6667L9.99996 16.5L15.8333 10.6667Z" stroke="black" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              <div className="thumbnails">
                <div className="carrosel-imagem">
                  {imagens.map((img, i) => (
                    <img
                      key={i}
                      src={img.url}
                      alt={`${produto.nome} thumb ${i}`}
                      onClick={() => setPrimaryImg(img.url)}
                      className={img.url === primaryImg ? 'active' : ''}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className='info-geral'>

              <div className="info">
                <div className='principal-info'>
                  <h1>{produto.nome}</h1>
                </div>
                {userId && (
                  <div className="preco">
                    R$ {variation.preco}
                  </div>
                )}
                {userId && deveExibirPrecoUnitario(variation.unidades_por_pacote) && (
                  <div className="preco-unitario">
                    {formatarPrecoUnitario(variation.preco, variation.unidades_por_pacote)} / unidade
                  </div>
                )}

                {showVariacoes && (
                  <div className="selector space-y-4">
                    {coresAll.length > 0 && (
                      <div className="variacao-cor">
                        <div className='title-cor'>
                          <p className="label">Cor:</p>
                        </div>
                        <div className="buttons flex space-x-2">
                          {coresAll.map(c => {
                            const disponivel = optionDisponivel('cor', c);
                            return (
                              <button
                                key={c}
                                type="button"
                                disabled={!disponivel}
                                onClick={() => {
                                  setLastChanged('cor');
                                  setCorSel(c);
                                }}
                                className={`px-2 py-1 border rounded-full focus:outline-none
                            ${corSel === c ? 'border-black font-semibold' : 'border-gray-300'}
                            ${!disponivel ? 'opcao-esgotada' : ''}`}
                              >
                                {c}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* TAMANHOS como botões */}
                    {tamAll.length > 0 && (
                      <div className="variacao-tamanho">
                        <div className='title-tamanho'>
                          <p className="label">Tamanho:</p>
                        </div>
                        <div className="buttons flex space-x-2">
                          {tamAll.map(t => {
                            const disponivel = optionDisponivel('tamanho', t);
                            return (
                              <button
                                key={t}
                                type="button"
                                disabled={!disponivel}
                                onClick={() => {
                                  setLastChanged('tamanho');
                                  setTamSel(t);
                                }}
                                className={`px-3 py-1 border rounded focus:outline-none
                          ${tamSel === t ? 'border-black font-semibold' : 'border-gray-300'}
                          ${!disponivel ? 'opcao-esgotada' : ''}`}
                              >
                                {t}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* MATERIAIS como botões */}
                    {matAll.length > 0 && (
                      <div className="variacao-material">
                        <div className='title-material'>
                          <p className="label">Material:</p>
                        </div>
                        <div className="buttons flex space-x-2">
                          {matAll.map(m => {
                            const disponivel = optionDisponivel('material', m);
                            return (
                              <button
                                key={m}
                                type="button"
                                disabled={!disponivel}
                                onClick={() => {
                                  setLastChanged('material');
                                  setMatSel(m);
                                }}
                                className={`px-3 py-1 border rounded focus:outline-none
                          ${matSel === m ? 'border-black font-semibold' : 'border-gray-300'}
                          ${!disponivel ? 'opcao-esgotada' : ''}`}
                              >
                                {m}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* TIPOS como botões */}
                    {tipoAll.length > 0 && (
                      <div className="variacao-tipo">
                        <div className='title-tipo'>
                          <p className="label">Tipo:</p>
                        </div>
                        <div className="buttons flex space-x-2">
                          {tipoAll.map(t => {
                            const disponivel = optionDisponivel('tipo', t);
                            return (
                              <button
                                key={t}
                                type="button"
                                disabled={!disponivel}
                                onClick={() => {
                                  setLastChanged('tipo');
                                  setTipoSel(t);
                                }}
                                className={`px-3 py-1 border rounded focus:outline-none
                          ${tipoSel === t ? 'border-black font-semibold' : 'border-gray-300'}
                          ${!disponivel ? 'opcao-esgotada' : ''}`}
                              >
                                {t}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <input type="hidden" name="variacaoId" value={selectedVar} />
                  </div>
                )}
                <div
                  className="descricao descricao-formatada"
                  dangerouslySetInnerHTML={{ __html: descricaoCurtaHtml }}
                />
              </div>

              <div className='info-estoqueProd'>
                {userId ? (
                <>
                <div className="bnt-carrinho">
                  {inCart ? (
                    <div className="controle-carrinho">
                      <div className="produto-no-carrinho-icon" title="Produto no carrinho">
                        <svg width="46" height="47" viewBox="0 0 46 47" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M18.3085 42.4369C18.3085 44.9466 16.2488 47 13.7313 47C11.2139 47 9.15423 44.9466 9.15423 42.4369C9.15423 39.9272 11.2139 37.8738 13.7313 37.8738C16.2488 37.8738 18.3085 39.9272 18.3085 42.4369ZM36.6169 37.8738C34.0995 37.8738 32.0398 39.9272 32.0398 42.4369C32.0398 44.9466 34.0995 47 36.6169 47C39.1343 47 41.194 44.9466 41.194 42.4369C41.194 39.9272 39.1343 37.8738 36.6169 37.8738ZM14.1891 30.5728V30.3447L16.2488 26.466H33.1841C34.7861 26.466 36.3881 25.5534 37.0746 24.1845L46 8.21359L42.1094 5.93204L33.1841 21.9029H17.1642L7.55224 1.36893H0V5.93204H4.57711L12.8159 23.2718L9.61194 28.7476C9.38308 29.432 9.15423 30.1165 9.15423 31.0291C9.15423 33.5388 11.2139 35.5922 13.7313 35.5922H41.194V31.0291H14.6468C14.4179 31.0291 14.1891 30.801 14.1891 30.5728ZM38.9055 3.19417L35.7015 0L24.7164 10.9515L18.7662 5.01942L15.5622 8.21359L24.7164 17.3398L38.9055 3.19417Z"
                            fill="white"
                          />
                        </svg>
                        <p>Produto no carrinho</p>
                      </div>

                      <div className="bnts-prods">
                        <button
                          className="bnt-mais"
                          disabled={!podeComprar}
                          onClick={() => updateQuantity(selectedVar, +1)}
                        >
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                              d="M7.00065 0.958008C7.57565 0.958008 8.04232 1.42467 8.04232 1.99967V5.95801H12.0007C12.2769 5.95801 12.5419 6.06775 12.7372 6.2631C12.9326 6.45846 13.0423 6.72341 13.0423 6.99967C13.0423 7.27594 12.9326 7.54089 12.7372 7.73624C12.5419 7.93159 12.2769 8.04134 12.0007 8.04134H8.04232V11.9997C8.04232 12.2759 7.93257 12.5409 7.73722 12.7362C7.54187 12.9316 7.27692 13.0413 7.00065 13.0413C6.72438 13.0413 6.45943 12.9316 6.26408 12.7362C6.06873 12.5409 5.95898 12.2759 5.95898 11.9997V8.04134H2.00065C1.72438 8.04134 1.45943 7.93159 1.26408 7.73624C1.06873 7.54089 0.958984 7.27594 0.958984 6.99967C0.958984 6.72341 1.06873 6.45846 1.26408 6.2631C1.45943 6.06775 1.72438 5.95801 2.00065 5.95801H5.95898V1.99967C5.95898 1.42467 6.42565 0.958008 7.00065 0.958008Z"
                              fill="white"
                            />
                            <path
                              d="M7.00065 0.958008C7.57565 0.958008 8.04232 1.42467 8.04232 1.99967V5.95801H12.0007C12.2769 5.95801 12.5419 6.06775 12.7372 6.2631C12.9326 6.45846 13.0423 6.72341 13.0423 6.99967C13.0423 7.27594 12.9326 7.54089 12.7372 7.73624C12.5419 7.93159 12.2769 8.04134 12.0007 8.04134H8.04232V11.9997C8.04232 12.2759 7.93257 12.5409 7.73722 12.7362C7.54187 12.9316 7.27692 13.0413 7.00065 13.0413C6.72438 13.0413 6.45943 12.9316 6.26408 12.7362C6.06873 12.5409 5.95898 12.2759 5.95898 11.9997V8.04134H2.00065C1.72438 8.04134 1.45943 7.93159 1.26408 7.73624C1.06873 7.54089 0.958984 7.27594 0.958984 6.99967C0.958984 6.72341 1.06873 6.45846 1.26408 6.2631C1.45943 6.06775 1.72438 5.95801 2.00065 5.95801H5.95898V1.99967C5.95898 1.42467 6.42565 0.958008 7.00065 0.958008Z"
                              fill="white"
                            />
                          </svg>
                        </button>

                        <div className="input-qtd">
                          <input
                            type="number"
                            min="1"
                            value={quantidade}
                            onChange={(e) => {
                              const novaQuantidade = parseInt(e.target.value, 10);
                              if (!isNaN(novaQuantidade) && novaQuantidade > 0) {
                                updateQuantity(selectedVar, novaQuantidade - quantidade);
                              }
                            }}
                          />
                        </div>

                        {/* Botão “–” */}
                        <button
                          className="bnt-menos"
                          onClick={() => updateQuantity(selectedVar, -1)}
                        >
                          <svg width="13" height="3" viewBox="0 0 13 3" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M1.38932 0H5.34766H11.3893C11.6656 0 11.9305 0.109747 12.1259 0.305097C12.3212 0.500447 12.431 0.765399 12.431 1.04167C12.431 1.31793 12.3212 1.58289 12.1259 1.77824C11.9305 1.97359 11.6656 2.08333 11.3893 2.08333H7.43099H1.38932C1.11306 2.08333 0.848104 1.97359 0.652753 1.77824C0.457403 1.58289 0.347656 1.31793 0.347656 1.04167C0.347656 0.765399 0.457403 0.500447 0.652753 0.305097C0.848104 0.109747 1.11306 0 1.38932 0Z"
                              fill="white"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      className="add-cart"
                      disabled={!podeComprar}
                      onClick={() =>
                        addToCart({
                          id: variation.id,
                          id_produto: produto.id,
                          nome: produto.nome,
                          preco: variation.preco,
                          imagem_principal: variation.imagens[0]?.url || '',
                          cor: variation.cor || '',
                          tamanho: variation.tamanho || '',
                          material: variation.material || '',
                          tipo: variation.tipo || '',
                          estoque: variation.estoque || 0,
                          unidades_por_pacote: variation.unidades_por_pacote || 1,
                          quantidade: 1
                        })
                      }
                    >
                      <svg width="45" height="47" viewBox="0 0 45 47" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22.3214 17.9048H26.7857V11.1905H33.4821V6.71429H26.7857V0H22.3214V6.71429H15.625V11.1905H22.3214V17.9048ZM13.3929 38.0476C10.9375 38.0476 8.95089 40.0619 8.95089 42.5238C8.95089 44.9857 10.9375 47 13.3929 47C15.8482 47 17.8571 44.9857 17.8571 42.5238C17.8571 40.0619 15.8482 38.0476 13.3929 38.0476ZM35.7143 38.0476C33.2589 38.0476 31.2723 40.0619 31.2723 42.5238C31.2723 44.9857 33.2589 47 35.7143 47C38.1696 47 40.1786 44.9857 40.1786 42.5238C40.1786 40.0619 38.1696 38.0476 35.7143 38.0476ZM15.8482 26.8571H32.4777C34.1518 26.8571 35.625 25.9395 36.3839 24.5519L45 8.86286L41.1161 6.71429L32.4777 22.381H16.808L7.29911 2.2381H0V6.71429H4.46429L12.5 23.7014L9.48661 29.1624C7.85714 32.1614 10 35.8095 13.3929 35.8095H40.1786V31.3333H13.3929L15.8482 26.8571Z" fill="white" />
                      </svg>
                      Adicionar ao carrinho
                    </button>
                  )}
                </div>
                <div className={`estoque ${produtoIndisponivel || variacaoIndisponivel ? 'indisponivel' : ''}`}>
                  {produtoIndisponivel
                    ? 'Produto indisponivel'
                    : variacaoIndisponivel
                      ? 'Variacao indisponivel'
                      : `Estoque: ${variation.estoque}`}
                </div>
                </>
                ) : (
                  <a
                    className="botao-whatsapp"
                    href={`https://wa.me/5511997636154?text=Olá!%20Tenho%20interesse%20no%20produto%20${encodeURIComponent(produto.nome)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg width="39" height="39" viewBox="0 0 39 39" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0.907305 38.4919L10.6554 35.9295C13.3885 37.4 16.4537 38.1814 19.575 38.1814C29.9506 38.1814 38.4063 29.7257 38.4063 19.3501C38.4063 14.3154 36.4489 9.58321 32.896 6.03C31.1535 4.27058 29.0782 2.87553 26.7912 1.92613C24.5041 0.976711 22.0509 0.491952 19.5746 0.500101C9.1992 0.500327 0.74375 8.95592 0.74375 19.3314C0.74375 22.6216 1.59953 25.8188 3.20039 28.6466L0.602537 38.1844L0.488825 38.6019L0.907305 38.4919ZM11.074 32.0458L5.64271 33.4733L7.08492 28.1909L7.11431 28.0833L7.05382 27.9896L6.68061 27.4111C6.68032 27.4107 6.68002 27.4102 6.67972 27.4097C5.16309 24.988 4.35796 22.1886 4.35625 19.3312C4.35633 10.9568 11.1819 4.13135 19.5562 4.13135C23.6148 4.13135 27.4322 5.71737 30.2914 8.59506L30.2924 8.59606C31.7082 10.005 32.8302 11.681 33.5933 13.5268C34.3563 15.3727 34.7452 17.3518 34.7375 19.3491L34.7375 19.3512C34.7744 27.7242 27.95 34.5313 19.575 34.5313C16.8453 34.5313 14.1707 33.7937 11.8278 32.4102C11.8276 32.4101 11.8273 32.41 11.8271 32.4098L11.2661 32.0732L11.1758 32.019L11.074 32.0458ZM24.9759 21.4812L24.976 21.4809L24.9634 21.4765C24.7563 21.4044 24.5203 21.3256 24.2865 21.3516C24.0258 21.3806 23.8138 21.5297 23.6272 21.7931L23.6272 21.7931L23.6245 21.797C23.3154 22.2516 22.4437 23.2911 22.1806 23.5917L22.1805 23.5917L22.1758 23.5974C22.0698 23.7261 21.9875 23.7663 21.9111 23.7752C21.8228 23.7854 21.6893 23.7603 21.4716 23.647L21.4716 23.6469L21.4644 23.6435C21.3926 23.609 21.2997 23.569 21.1877 23.5208C20.5419 23.2427 19.2653 22.6931 17.7916 21.3762L17.7914 21.376C16.4373 20.1683 15.5357 18.6819 15.2539 18.2123C15.1512 18.0276 15.1622 17.926 15.186 17.8597C15.2181 17.7704 15.2928 17.6858 15.4205 17.5581C15.5694 17.4093 15.75 17.191 15.9171 16.9891C15.9912 16.8996 16.0625 16.8134 16.1273 16.7378C16.354 16.4733 16.4627 16.2545 16.5919 15.9945C16.6043 15.9694 16.6169 15.944 16.6299 15.9182L16.6299 15.9182L16.6325 15.9128C16.8283 15.4966 16.7223 15.1481 16.5924 14.8883C16.4859 14.6753 15.5537 12.4018 15.1759 11.4573L15.1759 11.4572L15.1745 11.4539C14.9754 10.9759 14.7503 10.7154 14.4919 10.5912C14.2953 10.4967 14.0831 10.4959 13.9689 10.4955C13.9446 10.4954 13.9247 10.4954 13.9104 10.4944L13.9021 10.4939H13.8938H12.9937C12.6168 10.4939 12.0583 10.6276 11.5723 11.1558L11.5722 11.1558L11.5686 11.1599C11.5373 11.1955 11.501 11.2353 11.4607 11.2795C10.9727 11.8146 9.89375 12.9977 9.89375 15.2064C9.89375 16.4064 10.33 17.5724 10.7834 18.4649C11.2339 19.3517 11.7123 19.9895 11.8338 20.1514L11.8352 20.1533C11.8448 20.167 11.8592 20.1881 11.8783 20.216C12.3252 20.8701 15.3513 25.2985 19.8688 27.248C20.9859 27.7402 21.8577 28.0246 22.5376 28.2324C23.6943 28.6045 24.749 28.5438 25.5721 28.429L25.5736 28.4287C26.0709 28.3562 26.794 28.0562 27.4352 27.644C28.0696 27.2362 28.6879 26.6778 28.9045 26.0521C29.1083 25.4887 29.2114 24.9663 29.246 24.553C29.2633 24.3465 29.2639 24.1631 29.2499 24.013C29.237 23.8749 29.209 23.7272 29.1422 23.6221C29.0413 23.4636 28.8914 23.3636 28.7346 23.2802C28.6417 23.2307 28.5264 23.1775 28.4003 23.1193C28.3228 23.0835 28.2412 23.0458 28.1582 23.006C28.0479 22.9531 27.8306 22.8471 27.5582 22.7144C26.7 22.2963 25.2959 21.6121 24.9759 21.4812ZM25.5375 28.1815L25.5375 28.1815L25.5375 28.1815Z" strokeWidth="0.5" /></svg>
                    Chamar no WhatsApp
                  </a>
                )}
              </div>
            </div>
          </div>
          <div className='info-completa'>

            <div className="tabs">
              <button
                className={tab === 'descricao' ? 'active' : ''}
                onClick={() => setTab('descricao')}
              >
                Descrição
              </button>
              <button
                className={tab === 'detalhes' ? 'active' : ''}
                onClick={() => setTab('detalhes')}
              >
                Detalhes
              </button>
              <button
                className={tab === 'avaliacoes' ? 'active' : ''}
                onClick={() => setTab('avaliacoes')}
              >
                Avaliações
              </button>

            </div>

            <div className="tab-content">

              {tab === 'descricao' && (
                <div className="descricao-content">
                  {/* aba DESCRIÇÃO: exibe a descrição longa */}
                  <div
                    className="descricao-formatada"
                    dangerouslySetInnerHTML={{ __html: descricaoLongaHtml }}
                  />
                </div>
              )}

              {tab === 'detalhes' && (
                <div className="detalhes-content">
                  {/* aba DETALHES: mostra tabela com info do produto + info da variação */}
                  <h2>Detalhes do Produto</h2>
                  <table>
                    <tbody>
                      <tr>
                        <th>Nome do Produto</th>
                        <td>{produto.nome || '-'}</td>
                      </tr>
                      <tr>
                        <th>Código de Barras</th>
                        <td>{produto.codigo_barras || '-'}</td>
                      </tr>
                      <tr>
                        <th>Marca</th>
                        <td>{produto.marca || '-'}</td>
                      </tr>
                      <tr>
                        <th>Categoria</th>
                        <td>{produto.categoria_nome || '-'}</td>
                      </tr>
                    </tbody>
                  </table>

                  <h2 className="mt-4">Detalhes da Variação</h2>
                  <table>
                    <tbody>
                      <tr>
                        <th>Tamanho</th>
                        <td>{variation.tamanho || '-'}</td>
                      </tr>
                      <tr>
                        <th>Cor</th>
                        <td>{variation.cor || '-'}</td>
                      </tr>
                      <tr>
                        <th>Material</th>
                        <td>{variation.material || '-'}</td>
                      </tr>
                      {variation.tipo && (
                        <tr>
                          <th>Tipo</th>
                          <td>{variation.tipo}</td>
                        </tr>
                      )}
                      <tr>
                        <th>Unidades por pacote</th>
                        <td>{variation.unidades_por_pacote ? `${variation.unidades_por_pacote} unidades` : '-'}</td>
                      </tr>
                      <tr>
                        <th>Estoque</th>
                        <td>{variation.estoque != null ? `${variation.estoque} unidades` : '-'}</td>
                      </tr>
                      <tr>
                        <th>Peso (kg)</th>
                        <td>{variation.peso ? `${variation.peso} kg` : '-'}</td>
                      </tr>
                      <tr>
                        <th>Altura (cm)</th>
                        <td>{variation.altura ? `${variation.altura} cm` : '-'}</td>
                      </tr>
                      <tr>
                        <th>Largura (cm)</th>
                        <td>{variation.largura ? `${variation.largura} cm` : '-'}</td>
                      </tr>
                      <tr>
                        <th>Profundidade (cm)</th>
                        <td>{variation.profundidade ? `${variation.profundidade} cm` : '-'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {tab === 'avaliacoes' && (
                <div className="avaliacoes-content">
                  {produto.avaliacoes && produto.avaliacoes.length > 0 ? (
                    produto.avaliacoes.map(av => (
                      <div
                        key={av.id_avaliacao}
                        className="review"
                        style={{
                          borderBottom: '1px solid #e0e0e0',
                          padding: '1rem 0'
                        }}
                      >
                        <strong style={{ fontFamily: 'Lexend', color: '#131C46' }}>
                          Usuário #{av.avaliador_id} — Nota: {av.nota}/5
                        </strong>
                        <small style={{ color: '#777', fontSize: '0.85rem' }}>
                          {new Date(av.avaliacao_data).toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })}
                        </small>
                        <p style={{ marginTop: '0.5rem', fontFamily: 'Lexend' }}>
                          {av.comentario}
                        </p>
                      </div>
                    ))
                  ) : (
                    <p>Nenhuma avaliação disponível para este produto.</p>
                  )}
                </div>
              )}

            </div>
          </div>

          <div className="faixa-2">
            <div className='div-tituloOutrosProdutos'>
              <h1>Outros Produtos</h1>
            </div>
            <div className="outros-produtos-grid">
              {produtosRelacionados.length > 0 ? (
                produtosRelacionados.map(prod => (
                  <CardProduto
                    key={prod.id || prod.id_produto}
                    prod={{
                      ...prod,
                      id: prod.id_produto ?? prod.id
                    }}
                  />
                ))
              ) : (
                <p className="nenhum-produto">Nenhum produto disponível.</p>
              )}
            </div>
          </div>

          {produtosRecentes.length > 5 && (
            <div className="faixa-2">
              <div className='div-tituloOutrosProdutos'>
                <h1>Vistos Recentemente</h1>
              </div>
              <div className="outros-produtos-grid vistos-recentes-grid">
                {produtosRecentes.map(prod => (
                  <CardProduto
                    key={`recente-${prod.id_produto}-${prod.id_variacao}`}
                    prod={{
                      ...prod,
                      id: prod.id_produto
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {lightboxAberto && (
            <div className="lightbox-overlay" onClick={fecharLightbox}>
              <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
                <button type="button" className="lightbox-close" onClick={fecharLightbox}>Fechar</button>

                <button
                  type="button"
                  className="lightbox-nav lightbox-prev"
                  onClick={() => trocarImagemPorPasso(-1)}
                >
                  Anterior
                </button>

                <div
                  className="lightbox-image-wrap"
                  onWheel={onWheelZoom}
                  onTouchStart={(e) => iniciarArraste(e.touches[0].clientX)}
                  onTouchEnd={(e) => finalizarArraste(e.changedTouches[0].clientX)}
                >
                  <img
                    className="lightbox-image"
                    src={primaryImg}
                    alt={`${produto.nome} ampliado`}
                    style={{ transform: `scale(${zoomNivel})` }}
                  />
                </div>

                <button
                  type="button"
                  className="lightbox-nav lightbox-next"
                  onClick={() => trocarImagemPorPasso(1)}
                >
                  Proxima
                </button>

                <div className="lightbox-controls">
                  <button type="button" onClick={zoomOut}>-</button>
                  <span>{Math.round(zoomNivel * 100)}%</span>
                  <button type="button" onClick={zoomIn}>+</button>
                  <button type="button" onClick={resetZoom}>Reset</button>
                </div>
              </div>
            </div>
          )}
        </section>
      </main>
      <Rodape />
    </>
  );
}